import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatWhen, lifecycleLabel } from "@/lib/format";
import type { ProjectActivity, ProjectActivityWindow } from "@/lib/types";
import { Button } from "@/components/Button";
import { BarChart } from "@/components/icons";
import { Field } from "@/components/Field";
import { StatePanel } from "@/components/StatePanel";
import { StatusRail } from "@/components/StatusRail";
import { useLocale } from "@/locale/LocaleContext";
import ui from "@/components/ui.module.css";
import styles from "@/pages/app/activity.module.css";

function isWindow(value: string): value is ProjectActivityWindow {
  return value === "24h" || value === "7d";
}

function taskRail(state: string): string {
  if (state === "succeeded") return "succeeded";
  if (state === "failed" || state === "canceled") return "failed";
  if (state === "running" || state === "cancel_requested") return "running";
  return "queued";
}

function formatCount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

function formatScaleTick(value: number, locale: string): string {
  const fractionDigits = Number.isInteger(value) ? 0 : 1;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** Y-scale ticks without duplicates (e.g. max=1 must not render 1,1,0). */
function scaleTicksForMax(max: number): number[] {
  if (max <= 1) return [max, 0];
  const mid = max / 2;
  if (mid === max || mid === 0) return [max, 0];
  return [max, mid, 0];
}

/** Full UTC label for table/tooltips. */
function formatBucketLabel(
  iso: string,
  activityWindow: ProjectActivityWindow,
  locale: string,
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (activityWindow === "24h") {
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
      hour12: false,
    }).format(d);
  }
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(d);
}

/** Sparse chart axis: every 4h + UTC midnight (+ first bucket). */
function chartAxisLabel(
  iso: string,
  index: number,
  activityWindow: ProjectActivityWindow,
  locale: string,
): string | null {
  if (activityWindow === "7d") {
    return formatBucketLabel(iso, activityWindow, locale);
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const hour = d.getUTCHours();
  const minute = d.getUTCMinutes();
  const isMidnight = hour === 0 && minute === 0;
  const isFourHour = minute === 0 && hour % 4 === 0;
  if (!(index === 0 || isMidnight || isFourHour)) return null;
  if (isMidnight || index === 0) {
    return formatBucketLabel(iso, activityWindow, locale);
  }
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    hour12: false,
  }).format(d);
}

type Props = {
  projectId: string;
};

export function ProjectActivityPanel({ projectId }: Props) {
  const { m, locale, fmt } = useLocale();
  const chartId = useId();
  const [selectedWindow, setSelectedWindow] = useState<ProjectActivityWindow>("24h");
  const [data, setData] = useState<ProjectActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const projectIdRef = useRef(projectId);
  const loadFailedRef = useRef(m.projects.activityFailed);
  projectIdRef.current = projectId;
  loadFailedRef.current = m.projects.activityFailed;

  useEffect(() => {
    setData(null);
    setError(null);
    setLoading(true);
    setSelectedWindow("24h");
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    const capturedProjectId = projectId;
    const capturedWindow = selectedWindow;
    setLoading(true);
    setError(null);
    api
      .projectActivity(capturedProjectId, { window: capturedWindow }, ac.signal)
      .then((res) => {
        if (!alive || projectIdRef.current !== capturedProjectId) return;
        setData(res);
        setError(null);
      })
      .catch((e) => {
        if (!alive || ac.signal.aborted) return;
        if (projectIdRef.current !== capturedProjectId) return;
        setData(null);
        setError(e instanceof Error ? e.message : loadFailedRef.current);
      })
      .finally(() => {
        if (alive && projectIdRef.current === capturedProjectId) setLoading(false);
      });
    return () => {
      alive = false;
      ac.abort();
    };
  }, [projectId, selectedWindow, retry]);

  // Never present a previous period as the selected window.
  const displayData =
    data && data.window === selectedWindow && data.project_id === projectId ? data : null;

  const maxSeries = useMemo(() => {
    if (!displayData) return 1;
    return Math.max(
      1,
      ...displayData.series.map((p) => Math.max(p.created, p.succeeded, p.failed)),
    );
  }, [displayData]);

  const scaleTicks = useMemo(() => scaleTicksForMax(maxSeries), [maxSeries]);

  const chartSummary = displayData
    ? fmt(m.projects.activityChartSummary, {
        window:
          displayData.window === "24h"
            ? m.projects.activityWindow24h
            : m.projects.activityWindow7d,
        created: formatCount(
          displayData.series.reduce((n, p) => n + p.created, 0),
          locale,
        ),
        succeeded: formatCount(
          displayData.series.reduce((n, p) => n + p.succeeded, 0),
          locale,
        ),
        failed: formatCount(
          displayData.series.reduce((n, p) => n + p.failed, 0),
          locale,
        ),
      })
    : "";

  function onWindowChange(next: string) {
    if (!isWindow(next) || next === selectedWindow) return;
    setData(null);
    setError(null);
    setSelectedWindow(next);
  }

  function onRefresh() {
    setRetry((n) => n + 1);
  }

  const windowLabel =
    selectedWindow === "24h" ? m.projects.activityWindow24h : m.projects.activityWindow7d;

  return (
    <section className={ui.panel} style={{ marginTop: "1.25rem" }} aria-labelledby="project-activity">
      <h2 id="project-activity" className={`display ${ui.sectionHead}`}>
        <BarChart size={18} aria-hidden />
        {m.projects.activityTitle}
      </h2>
      <p className={ui.meta} style={{ marginTop: "0.35rem" }}>
        {m.projects.activityLead}
      </p>

      <div className={styles.toolbar}>
        <Field label={m.projects.activityWindow} htmlFor="activity-window">
          <select
            id="activity-window"
            className={ui.input}
            value={selectedWindow}
            onChange={(e) => onWindowChange(e.target.value)}
          >
            <option value="24h">{m.projects.activityWindow24h}</option>
            <option value="7d">{m.projects.activityWindow7d}</option>
          </select>
        </Field>
        <div className={styles.toolbarActions}>
          {displayData ? (
            <p className={ui.meta} style={{ margin: 0 }}>
              {fmt(m.projects.activityObserved, {
                when: formatWhen(displayData.observed_at, locale),
              })}
              {" · "}
              {m.projects.activitySource}
            </p>
          ) : (
            <p className={ui.meta} style={{ margin: 0 }}>
              {windowLabel}
            </p>
          )}
          <Button
            type="button"
            variant="secondary"
            disabled={loading}
            aria-busy={loading || undefined}
            onClick={onRefresh}
          >
            {loading ? m.projects.activityRefreshing : m.projects.activityRefresh}
          </Button>
        </div>
      </div>

      {loading && !displayData ? <StatePanel kind="pending" title={m.common.loading} /> : null}

      {error ? (
        <div role="alert" style={{ marginTop: "0.75rem" }}>
          <p className={ui.fieldError}>{error}</p>
          <Button type="button" variant="secondary" onClick={onRefresh}>
            {m.projects.activityRetry}
          </Button>
        </div>
      ) : null}

      {displayData && !error ? (
        <div aria-busy={loading || undefined}>
          {loading ? (
            <p className={ui.meta} role="status" style={{ marginTop: "0.65rem" }}>
              {m.projects.activityRefreshing}
            </p>
          ) : null}

          <div className={styles.counts} aria-label={m.projects.activityTaskCounts}>
            <div className={styles.count}>
              <div className={styles.countLabel}>{m.projects.activityTotal}</div>
              <div className={styles.countValue}>{formatCount(displayData.tasks.total, locale)}</div>
            </div>
            <div className={styles.count}>
              <div className={styles.countLabel}>{lifecycleLabel(m, "queued")}</div>
              <div className={styles.countValue}>{formatCount(displayData.tasks.queued, locale)}</div>
            </div>
            <div className={styles.count}>
              <div className={styles.countLabel}>{lifecycleLabel(m, "running")}</div>
              <div className={styles.countValue}>{formatCount(displayData.tasks.running, locale)}</div>
            </div>
            <div className={styles.count}>
              <div className={styles.countLabel}>{lifecycleLabel(m, "cancel_requested")}</div>
              <div className={styles.countValue}>
                {formatCount(displayData.tasks.cancel_requested, locale)}
              </div>
            </div>
            <div className={styles.count}>
              <div className={styles.countLabel}>{lifecycleLabel(m, "succeeded")}</div>
              <div className={styles.countValue}>
                {formatCount(displayData.tasks.succeeded, locale)}
              </div>
            </div>
            <div className={styles.count}>
              <div className={styles.countLabel}>{lifecycleLabel(m, "failed")}</div>
              <div className={styles.countValue}>{formatCount(displayData.tasks.failed, locale)}</div>
            </div>
            <div className={styles.count}>
              <div className={styles.countLabel}>{lifecycleLabel(m, "canceled")}</div>
              <div className={styles.countValue}>
                {formatCount(displayData.tasks.canceled, locale)}
              </div>
            </div>
          </div>

          <h3 className="display" style={{ fontSize: "1rem", margin: "1rem 0 0" }}>
            {m.projects.activityMacRequests}
          </h3>
          <div className={styles.counts} aria-label={m.projects.activityMacRequests}>
            <div className={styles.count}>
              <div className={styles.countLabel}>{lifecycleLabel(m, "requested")}</div>
              <div className={styles.countValue}>
                {formatCount(displayData.workspace_requests.requested, locale)}
              </div>
            </div>
            <div className={styles.count}>
              <div className={styles.countLabel}>{lifecycleLabel(m, "reviewing")}</div>
              <div className={styles.countValue}>
                {formatCount(displayData.workspace_requests.reviewing, locale)}
              </div>
            </div>
            <div className={styles.count}>
              <div className={styles.countLabel}>{lifecycleLabel(m, "declined")}</div>
              <div className={styles.countValue}>
                {formatCount(displayData.workspace_requests.declined, locale)}
              </div>
            </div>
            <div className={styles.count}>
              <div className={styles.countLabel}>{lifecycleLabel(m, "canceled")}</div>
              <div className={styles.countValue}>
                {formatCount(displayData.workspace_requests.canceled, locale)}
              </div>
            </div>
          </div>

          <h3 className="display" style={{ fontSize: "1rem", margin: "1rem 0 0" }} id={chartId}>
            {m.projects.activitySeriesTitle}
          </h3>
          <p className={ui.meta} style={{ marginTop: "0.25rem" }}>
            {m.projects.activitySeriesLead}
          </p>
          <p className={ui.meta} style={{ marginTop: "0.25rem" }}>
            {fmt(m.projects.activityScaleMax, { max: formatCount(maxSeries, locale) })}
            {" · "}
            {m.projects.activityUtcNote}
          </p>

          <div className={styles.chartFrame}>
            <div className={styles.scale} aria-hidden="true">
              {scaleTicks.map((tick, i) => (
                <span key={`scale-${tick}-${i}`}>{formatScaleTick(tick, locale)}</span>
              ))}
            </div>
            <div
              className={styles.chart}
              role="img"
              aria-labelledby={chartId}
              aria-describedby={`${chartId}-desc`}
            >
              <div
                className={styles.chartInner}
                style={{
                  minWidth:
                    displayData.window === "24h"
                      ? `max(100%, ${displayData.series.length * 1.5}rem)`
                      : undefined,
                }}
              >
                {displayData.series.map((point, index) => {
                  const axis = chartAxisLabel(point.at, index, displayData.window, locale);
                  const fullLabel = formatBucketLabel(point.at, displayData.window, locale);
                  return (
                    <div key={point.at} className={styles.bucket}>
                      <div className={styles.bars}>
                        <div
                          className={`${styles.bar} ${styles.barCreated}`}
                          style={{ height: `${(point.created / maxSeries) * 100}%` }}
                          title={`${fullLabel} UTC · ${m.projects.activityCreated}: ${formatCount(point.created, locale)}`}
                        />
                        <div
                          className={`${styles.bar} ${styles.barSucceeded}`}
                          style={{ height: `${(point.succeeded / maxSeries) * 100}%` }}
                          title={`${fullLabel} UTC · ${m.projects.activitySucceeded}: ${formatCount(point.succeeded, locale)}`}
                        />
                        <div
                          className={`${styles.bar} ${styles.barFailed}`}
                          style={{ height: `${(point.failed / maxSeries) * 100}%` }}
                          title={`${fullLabel} UTC · ${m.projects.activityFailedEvents}: ${formatCount(point.failed, locale)}`}
                        />
                      </div>
                      <div className={styles.bucketLabel} aria-hidden={axis ? undefined : true}>
                        {axis ?? "\u00a0"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <p id={`${chartId}-desc`} className="sr-only">
            {chartSummary}
          </p>
          <div className={styles.legend} aria-hidden="true">
            <span className={styles.legendItem}>
              <span className={`${styles.swatch} ${styles.barCreated}`} />
              {m.projects.activityCreated}
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.swatch} ${styles.barSucceeded}`} />
              {m.projects.activitySucceeded}
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.swatch} ${styles.barFailed}`} />
              {m.projects.activityFailedEvents}
            </span>
          </div>

          <details className={styles.tableDetails}>
            <summary>{m.projects.activityTableToggle}</summary>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <caption className="sr-only">{m.projects.activityTableCaption}</caption>
                <thead>
                  <tr>
                    <th scope="col">{m.projects.activityBucket}</th>
                    <th scope="col">{m.projects.activityCreated}</th>
                    <th scope="col">{m.projects.activitySucceeded}</th>
                    <th scope="col">{m.projects.activityFailedEvents}</th>
                  </tr>
                </thead>
                <tbody>
                  {displayData.series.map((point) => (
                    <tr key={`row-${point.at}`}>
                      <th scope="row">
                        {formatBucketLabel(point.at, displayData.window, locale)} UTC
                      </th>
                      <td>{formatCount(point.created, locale)}</td>
                      <td>{formatCount(point.succeeded, locale)}</td>
                      <td>{formatCount(point.failed, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          <p className={ui.meta} style={{ marginTop: "0.75rem" }}>
            {m.projects.activityCapsNote}
          </p>

          <h3 className="display" style={{ fontSize: "1rem", margin: "1rem 0 0.35rem" }}>
            {m.projects.activityRecent}
          </h3>
          {displayData.recent_tasks.length === 0 ? (
            <StatePanel kind="empty" title={m.projects.activityRecentEmpty} />
          ) : (
            <div className={styles.recent}>
              {displayData.recent_tasks.map((task) => (
                <Link key={task.id} to={`/app/tasks/${task.id}`} className={ui.row}>
                  <StatusRail state={taskRail(task.state)} />
                  <span>
                    <strong>{task.title}</strong>
                    <div className={ui.meta}>
                      {lifecycleLabel(m, task.state)} · {lifecycleLabel(m, task.kind)} ·{" "}
                      {formatWhen(task.updated_at, locale)}
                    </div>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
